import { cva, type VariantProps } from "class-variance-authority";
import "./profile.css";
import { forwardRef, type ComponentProps, useMemo } from "react";
import { cn } from "@core/utils/cn";

export type VariantColor =
  | "gray"
  | "white"
  | "blue"
  | "pink"
  | "orange"
  | "yellow"
  | "green"
  | "gradiant"
  | "random";

const profileStyles = cva(
  "flex items-center justify-center text-[1rem] font-bold uppercase transition-colors duration-200 border border-secondary-300",
  {
    variants: {
      shape: {
        circle: "rounded-full",
        square: "rounded-lg",
      },
      variantColor: {
        gray: "bg-secondary-100 text-secondary-900",
        white: "bg-white text-secondary-900",
        blue: "bg-accent-blue-100 text-accent-blue-800",
        pink: "bg-accent-pink-100 text-accent-pink-700",
        orange: "bg-accent-orange-100 text-accent-orange-700",
        yellow: "bg-accent-yellow-100 text-accent-yellow-700",
        green: "bg-accent-green-100 text-accent-green-700",
        gradiant:
          "bg-[linear-gradient(to_bottom,_#008A87,_#027574)] text-white",
        random: "", // placeholder
      },
      size: {
        sm: "size-6 text-sm",
        md: "size-8 text-sm",
        lg: "size-12 text-base",
        xl: "size-16 text-lg",
        "2xl": "size-20 text-xl",
      },
    },
    defaultVariants: {
      shape: "circle",
      size: "md",
      variantColor: "gray",
    },
  },
);

interface ProfileProps
  extends
    Omit<ComponentProps<"button">, "size">,
    VariantProps<typeof profileStyles> {}

const colorKeys: NonNullable<ProfileProps["variantColor"]>[] = [
  "gray",
  "blue",
  "pink",
  "orange",
];

const Avatar = forwardRef<HTMLButtonElement, ProfileProps>(
  (
    { shape, className, children, disabled, size, variantColor, ...props },
    ref,
  ) => {
    const finalColor = useMemo(() => {
      if (variantColor === "random") {
        const randomIndex = Math.floor(Math.random() * colorKeys.length);
        return colorKeys[randomIndex];
      }
      return variantColor;
    }, [variantColor]);

    return (
      <button
        ref={ref}
        className={cn(
          profileStyles({ shape, size, variantColor: finalColor }),
          "disabled:opacity-50 disabled:cursor-not-allowed",
          className,
        )}
        type="button"
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Avatar.displayName = "Avatar";

export default Avatar;
